import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Model } from "survey-core";
import { getActiveSurveyBySlug, submitSurveyResponse } from "../surveys/surveyApi";
import { surveyGamifiedV2 } from "../surveys/defaultSurveys";
import TypeformProgress from "../components/survey/TypeformProgress";
import TypeformNavigation from "../components/survey/TypeformNavigation";
import SurveyCard from "../components/survey/SurveyCard";
import SurveyPersonaCTA from "../components/survey/SurveyPersonaCTA";
import DOMPurify from "dompurify";
import { authService } from "../services/auth";
import "../styles/share-warning.css";

function escapeHtml(value: unknown): string {
  if (value === undefined || value === null) return "";
  const text = String(value);
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || "";

interface SurveyChoice {
  value: string;
  text: string;
  icon?: string;
}

interface SurveyData {
  survey_version_id?: string;
  definition_json: unknown;
}

interface ActiveSurveyResponse {
  survey_version_id: string;
  definition_json: unknown;
}

interface SurveyElement {
  name: string;
  title?: string;
  description?: string;
  isVisible?: boolean;
  isRequired?: boolean;
  getType?: () => string;
  type?: string;
  choices?: Array<SurveyChoice | string>;
  maxSelectedChoices?: number;
  rows?: Array<SurveyChoice | string>;
  columns?: Array<SurveyChoice | string>;
  html?: string;
}

interface SurveyPage {
  name: string;
  title?: string;
  description?: string;
  isVisible?: boolean;
  getElements?: () => SurveyElement[];
  elements?: SurveyElement[];
}

export function interpolateHtml(html: string, model: Model): string {
  return html.replace(/\{(\w+)\}/g, (_match, key) => {
    let val = model.getValue(key);
    if (val === undefined || val === null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const calc = (model as any).getCalculatedValueByName?.(key);
      if (calc) {
        val = calc.value;
      }
    }
    return escapeHtml(val);
  });
}

export function extractIcon(text: string): string {
  if (!text) return "";
  const match = text.match(
    /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(?:\u200d(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F))?/u
  );
  return match ? match[0] : "";
}

function getPersonaValue(model: Model, key: string): string {
  let val = model.getValue(key);
  if (val === undefined || val === null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const calc = (model as any).getCalculatedValueByName?.(key);
    if (calc) {
      val = calc.value;
    }
  }
  return val !== undefined && val !== null ? String(val) : "";
}

export function shareSurvey(model: Model) {
  const title = getPersonaValue(model, "persona_title") || "My Gamer Persona";
  const icon = getPersonaValue(model, "persona_icon");
  const description = getPersonaValue(model, "persona_description");
  const superpower = getPersonaValue(model, "persona_superpower");
  const match = getPersonaValue(model, "persona_match");
  const benefit1 = getPersonaValue(model, "benefit_text_1");
  const benefit2 = getPersonaValue(model, "benefit_text_2");
  const benefit3 = getPersonaValue(model, "benefit_text_3");

  const benefits = [benefit1, benefit2, benefit3].filter(Boolean);
  const benefitsText = benefits.length
    ? `\n\nWhy this fits me:\n${benefits.map((b) => `• ${b}`).join("\n")}`
    : "";

  const text = `${icon} I discovered my board-game persona: ${title}${match ? ` (${match}% match)` : ""}\n\n${description}${superpower ? `\n\nSuperpower: ${superpower}` : ""}${benefitsText}\n\nTake the quiz on Pista to find yours!`;

  const shareData = {
    title: `I got ${title} on Pista!`,
    text,
    url: window.location.href,
  };

  if (navigator.share) {
    navigator.share(shareData).catch(() => {
      // User cancelled or share failed; no-op is fine
    });
  } else if (navigator.clipboard) {
    void navigator.clipboard.writeText(
      `${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`
    );
  }
}

function getPageElements(page: SurveyPage | null): SurveyElement[] {
  if (!page) return [];
  if (typeof page.getElements === "function") {
    return page.getElements();
  }
  // Fallback for plain JSON page objects or older survey-core versions
  return page.elements || [];
}

function computePersonaValues(model: Model) {
  // Only run as a fallback when the loaded survey JSON lacks calculatedValues
  const hasCalcPersona =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (model as any).getCalculatedValueByName?.("persona_title") !== null;
  if (hasCalcPersona) return;
  const gameTypes: string[] = model.getValue("game_types") || [];
  const researchMethods: string[] = model.getValue("research_methods") || [];
  const playGroup: string[] = model.getValue("play_group") || [];
  const painPoint: string = model.getValue("pain_point") || "";

  let title: string;
  if (
    gameTypes.includes("strategy") &&
    researchMethods.includes("bgg_ratings") &&
    playGroup.includes("solo")
  ) {
    title = "The Solo Strategist";
  } else if (
    researchMethods.includes("digital_try") ||
    researchMethods.includes("cafe_try")
  ) {
    title = "The Try-Before-You-Buyer";
  } else if (
    playGroup.includes("friends_casual") ||
    playGroup.includes("family")
  ) {
    title = "The Social Gamer";
  } else if (
    painPoint === "decision_paralysis" ||
    painPoint === "rules_friction"
  ) {
    title = "The Host";
  } else {
    title = "The Curator";
  }

  const iconMap: Record<string, string> = {
    "The Solo Strategist": "🧘",
    "The Try-Before-You-Buyer": "🎮",
    "The Social Gamer": "🍻",
    "The Host": "🏠",
    "The Curator": "📚",
  };

  const descMap: Record<string, string> = {
    "The Solo Strategist":
      "You play to master systems, not to beat others. The only opponent you need is your last high score.",
    "The Try-Before-You-Buyer":
      "You never waste money on a game that doesn't fit. Your shelf is full of proven winners.",
    "The Social Gamer":
      "For you, games are about connection, not competition. Your laugh track is the best sound at the table.",
    "The Host":
      "Your table is always full because you make game night effortless. You're the glue that holds the group together.",
    "The Curator":
      "You don't just play games — you collect, study, and curate. Your game nights are legendary because you've already done the homework.",
  };

  const superpowerMap: Record<string, string> = {
    "The Solo Strategist": "Turning 2 hours of solitude into a deeply satisfying victory",
    "The Try-Before-You-Buyer": "Spotting a hidden gem before anyone else",
    "The Social Gamer": "Making everyone feel welcome at the table",
    "The Host": "Getting any group to the fun part in under 5 minutes",
    "The Curator": "Finding the perfect game for any mood or player count",
  };

  const shortMap: Record<string, string> = {
    "The Solo Strategist": "Solo Strategists",
    "The Try-Before-You-Buyer": "Try-Before-You-Buyers",
    "The Social Gamer": "Social Gamers",
    "The Host": "Hosts",
    "The Curator": "Curators",
  };

  const matchMap: Record<string, string> = {
    "The Solo Strategist": "94",
    "The Try-Before-You-Buyer": "87",
    "The Social Gamer": "91",
    "The Host": "89",
    "The Curator": "93",
  };

  const featuresMap: Record<string, string> = {
    "The Solo Strategist": "Play tracking + Stats dashboard + Solo mode finder",
    "The Try-Before-You-Buyer": "Online play badge + BGA integration + Price comparison",
    "The Social Gamer": "Party game recommendations + Shared collections + Player count optimizer",
    "The Host": "Mood matcher + Quick rules refresher + Decision engine",
    "The Curator": "Similarity search + Collection insights + Price tracker",
  };

  model.setValue("persona_title", title);
  model.setValue("persona_icon", iconMap[title] || "");
  model.setValue("persona_description", descMap[title] || "");
  model.setValue("persona_superpower", superpowerMap[title] || "");
  model.setValue("persona_title_short", shortMap[title] || "");
  model.setValue("persona_match", matchMap[title] || "");
  model.setValue("persona_features", featuresMap[title] || "");
}

function buildMeta(startedAtMs: number) {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_content: params.get("utm_content") || null,
    utm_term: params.get("utm_term") || null,
    referrer: document.referrer || null,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    device: {
      userAgent: navigator.userAgent,
      screen: { w: window.screen.width, h: window.screen.height },
    },
    started_at: new Date(startedAtMs).toISOString(),
    completion_ms: Date.now() - startedAtMs,
  };
}

interface SurveyUser {
  id: number;
  email?: string | null;
  username?: string | null;
  persona_title?: string | null;
  persona_icon?: string | null;
}

export default function TypeformSurvey({
  user = null,
  onPersonaUpdate,
}: {
  user?: SurveyUser | null;
  onPersonaUpdate?: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [currentPageName, setCurrentPageName] = useState<string>("");
  const [renderKey, setRenderKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        // Try to load from Supabase first
        const data = await getActiveSurveyBySlug("gamified") as ActiveSurveyResponse;
        if (mounted) {
          setSurveyData({
            survey_version_id: data.survey_version_id,
            definition_json: data.definition_json,
          });
        }
      } catch {
        // Fallback to default
        if (mounted) {
          setSurveyData({
            definition_json: surveyGamifiedV2,
          });
        }
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!surveyData) return;
    try {
      const definition =
        typeof surveyData.definition_json === "string"
          ? JSON.parse(surveyData.definition_json)
          : surveyData.definition_json;
      const surveyModel = new Model(definition);
      // Set default properties for Typeform experience
      const extendedModel = surveyModel as unknown as {
        showProgressBar?: boolean;
        showTitle?: boolean;
        showNavigationButtons?: string | boolean;
      };
      extendedModel.showProgressBar = false;
      extendedModel.showTitle = false;
      extendedModel.showNavigationButtons = "none";
      surveyModel.checkErrorsMode = "onValueChanged";
      setModel(surveyModel);
      const allPages = (surveyModel as unknown as { pages?: SurveyPage[] }).pages || [];
      const visible = allPages.filter((p) => p.isVisible !== false);
      setCurrentPageName(visible[0]?.name || allPages[0]?.name || "");
      setLoading(false);
    } catch (err: unknown) {
      setError("Failed to load survey: " + (err instanceof Error ? err.message : String(err)));
      setLoading(false);
    }
  }, [surveyData]);

  useEffect(() => {
    if (!model || currentPageName !== "page_result") return;
    computePersonaValues(model);
    setRenderKey((k) => k + 1);
  }, [model, currentPageName]);

  useEffect(() => {
    if (currentPageName !== "page_result") return;
    // Force a re-render so the survey HTML placeholders are in the DOM
    const id = setTimeout(() => setRenderKey((k) => k + 1), 0);
    return () => clearTimeout(id);
  }, [currentPageName]);

  useLayoutEffect(() => {
    // The app's scrollable container is .app-content (overflow-y: auto).
    // Reset it so every question page starts at the top.
    const appContent = document.querySelector<HTMLElement>(".app-content");
    if (appContent) {
      appContent.scrollTop = 0;
    }
    // Fallbacks for window / html / body scrolling
    if (window.scrollTo) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, [currentPageName]);

  const visiblePages = useMemo(() => {
    if (!model) return [];
    const all = (model as unknown as { pages?: SurveyPage[] }).pages || [];
    const filtered = all.filter((p) => p.isVisible !== false);
    return filtered.length > 0 ? filtered : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, renderKey]);

  const currentPageIndex = useMemo(() => {
    return visiblePages.findIndex((p) => p.name === currentPageName);
  }, [visiblePages, currentPageName]);

  const currentPage = useMemo(() => {
    return visiblePages.find((p) => p.name === currentPageName) || null;
  }, [visiblePages, currentPageName]);

  const canGoNext = useMemo(() => {
    if (!currentPage || !model) return false;
    const elements = getPageElements(currentPage).filter((el) => el.isVisible !== false);
    return elements.every((el) => {
      if (!el.isRequired) return true;
      const val = model.getValue(el.name);
      if (Array.isArray(val)) return val.length > 0;
      return val !== undefined && val !== null && val !== "";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, model, renderKey]);

  const handleSetValue = (name: string, value: unknown) => {
    if (!model) return;
    model.setValue(name, value);
    setRenderKey((k) => k + 1);
  };

  const handleToggleCheckbox = (name: string, value: string, maxChoices?: number) => {
    if (!model) return;
    const current = (model.getValue(name) as string[]) || [];
    let next: string[];
    if (current.includes(value)) {
      next = current.filter((v) => v !== value);
    } else {
      if (maxChoices && current.length >= maxChoices) return;
      next = [...current, value];
    }
    model.setValue(name, next);
    setRenderKey((k) => k + 1);
  };

  const handleNext = () => {
    if (!model) return;
    const idx = currentPageIndex;
    if (idx >= 0 && idx < visiblePages.length - 1) {
      const nextPage = visiblePages[idx + 1];
      setCurrentPageName(nextPage.name);
      setError("");
      if (nextPage.name === "page_result" && !submitted && !isSubmitting) {
        handleSubmit();
      }
    } else if (idx === visiblePages.length - 1) {
      if (!submitted && !isSubmitting) {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    const idx = currentPageIndex;
    if (idx > 0) {
      setCurrentPageName(visiblePages[idx - 1].name);
      setError("");
    }
  };

  const handleSubmit = async () => {
    if (!model || !surveyData) return;
    if (!surveyData.survey_version_id) {
      setError("Survey version not found. Your response could not be saved.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      const answers = model.getAllValues();
      const meta = buildMeta(startTimeRef.current);
      await submitSurveyResponse({
        supabaseUrl,
        surveyVersionId: surveyData.survey_version_id,
        answers,
        meta,
        turnstileToken: undefined,
      });
      setSubmitted(true);
      // Auto-save persona for authenticated users
      if (user) {
        const pTitle = getPersonaValue(model, "persona_title");
        const pIcon = getPersonaValue(model, "persona_icon");
        if (pTitle) {
          try {
            await authService.updatePersona(pTitle, pIcon);
            if (onPersonaUpdate) onPersonaUpdate();
          } catch {
            // Non-fatal: persona save failure should not disrupt survey completion
          }
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to submit survey.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-lg text-gray-600">Loading quiz...</div>
      </div>
    );
  }

  if (!model || !currentPage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="max-w-xl w-full text-center">
          <div className="text-lg text-red-600 mb-4">
            {error || "Survey could not be loaded."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen survey-background">
      {/* Header / Progress */}
      <div className="sticky top-0 z-10 px-4 pt-4 pb-2">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-white/20">
            <TypeformProgress current={currentPageIndex} total={visiblePages.length} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center px-4 py-6">
        <div className="max-w-2xl mx-auto w-full animate-slide-up bg-white rounded-3xl shadow-2xl overflow-hidden p-6 md:p-10" key={currentPageName}>
          {/* Page Title */}
          {currentPage.title && (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {currentPage.title}
            </h1>
          )}
          {currentPage.description && (
            <p className="text-gray-500 mb-8">{currentPage.description}</p>
          )}

          {currentPageName === "page_result" && (
            <div className="text-center mb-8 animate-fade-in">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Your game night superpower is unlocked!
              </h2>
              <p className="text-gray-600 mb-4">
                Thanks for taking the quiz. Check out Pista for personalized game recommendations.
              </p>
            </div>
          )}

          {/* Questions */}
          <div className="space-y-6">
            {getPageElements(currentPage).map((element) => {
              if (element.isVisible === false) return null;
              const type = element.getType ? element.getType() : element.type;
              const name = element.name;

              if (type === "radiogroup") {
                const choices = (element.choices || []) as SurveyChoice[];
                const selected = model.getValue(name) as string | undefined;
                return (
                  <div key={name}>
                    {element.title && (
                      <p className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
                        {element.title}
                      </p>
                    )}
                    {element.description && (
                      <p className="text-gray-500 mb-4">{element.description}</p>
                    )}
                    <div className="space-y-3">
                      {choices.map((choice) => {
                        const isSelected = selected === choice.value;
                        return (
                          <SurveyCard
                            key={choice.value}
                            text={choice.text || choice.value}
                            selected={isSelected}
                            dimmed={!!selected && !isSelected}
                            onClick={() => handleSetValue(name, isSelected ? null : choice.value)}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              }

              if (type === "checkbox") {
                const choices: Array<SurveyChoice | string> = element.choices || [];
                const selected = (model.getValue(name) as string[]) || [];
                const maxChoices = element.maxSelectedChoices;
                const selectedCount = selected.length;
                const counterText = maxChoices
                  ? `${selectedCount}/${maxChoices} selected`
                  : `${selectedCount} selected`;
                return (
                  <div key={name}>
                    {element.title && (
                      <p className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                        {element.title}
                      </p>
                    )}
                    {element.description && (
                      <p className="text-gray-500 mb-4">{element.description}</p>
                    )}
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto relative rounded-xl border border-gray-100 p-2">
                      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm py-2 text-sm font-semibold text-pista-700 text-center border-b border-gray-100 mb-2">
                        {counterText}
                      </div>
                      {choices.map((choice) => (
                        <SurveyCard
                          key={typeof choice === "string" ? choice : choice.value}
                          text={
                            typeof choice === "string" ? choice : choice.text || choice.value
                          }
                          selected={selected.includes(
                            typeof choice === "string" ? choice : choice.value
                          )}
                          onClick={() =>
                            handleToggleCheckbox(
                              name,
                              typeof choice === "string" ? choice : choice.value,
                              maxChoices
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              }

              if (type === "matrix") {
                const rows: Array<SurveyChoice | string> = element.rows || [];
                const columns: Array<SurveyChoice | string> = element.columns || [];
                const values = (model.getValue(name) as Record<string, string | number>) || {};
                return (
                  <div key={name}>
                    {element.title && (
                      <p className="text-lg md:text-xl font-semibold text-gray-800 mb-3">
                        {element.title}
                      </p>
                    )}
                    {/* Column legend: icon + label, shown once */}
                    <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
                      {columns.map((col) => {
                        const colText = typeof col === "string" ? col : col.text || col.value;
                        const colIcon = extractIcon(colText);
                        const colLabel = colIcon ? colText.slice(colIcon.length).trim() : colText;
                        return (
                          <span key={typeof col === "string" ? col : col.value} className="flex items-center gap-1">
                            <span className="text-base">{colIcon || "•"}</span>
                            <span>{colLabel}</span>
                          </span>
                        );
                      })}
                    </div>
                    <div className="space-y-3">
                      {rows.map((row) => {
                        const rowKey = typeof row === "string" ? row : row.value;
                        const rowText = typeof row === "string" ? row : row.text || row.value;
                        const rowIcon = (typeof row !== "string" ? row.icon : undefined) || extractIcon(rowText);
                        const colonIdx = rowText.indexOf(":");
                        const rowName = colonIdx > -1 ? rowText.slice(0, colonIdx).trim() : rowText;
                        const rowDesc = colonIdx > -1 ? rowText.slice(colonIdx + 1).trim() : "";
                        const displayName = rowIcon
                          ? (rowName.startsWith(rowIcon) ? rowName.slice(rowIcon.length).trim() : rowName)
                          : rowName;
                        return (
                          <div
                            key={rowKey}
                            className="bg-white border border-gray-200 rounded-xl p-3 flex gap-3 items-center"
                          >
                            {/* Left: game-card style — image area + name underneath */}
                            <div className="flex flex-col items-center gap-0 flex-shrink-0 w-16">
                              <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-3xl leading-none">
                                {rowIcon || "🎲"}
                              </div>
                              <span className="mt-1 text-[10px] font-semibold text-gray-600 leading-tight text-center w-full line-clamp-2">
                                {displayName}
                              </span>
                            </div>
                            {/* Right: description + evenly spaced icon-only buttons */}
                            <div className="flex-1 flex flex-col gap-2 min-w-0">
                              {rowDesc && (
                                <p className="text-sm text-gray-600 leading-snug">{rowDesc}</p>
                              )}
                              <div className="flex gap-2 justify-between">
                                {columns.map((col) => {
                                  const colKey = typeof col === "string" ? col : col.value;
                                  const colText = typeof col === "string" ? col : col.text || col.value;
                                  const colIcon = extractIcon(colText);
                                  const isSelected = values[rowKey] === colKey;
                                  return (
                                    <button
                                      key={colKey}
                                      type="button"
                                      title={colText}
                                      aria-label={colText}
                                      onClick={() => {
                                        const next = { ...values, [rowKey]: colKey };
                                        handleSetValue(name, next);
                                      }}
                                      className={`flex-1 h-10 flex items-center justify-center rounded-lg text-lg transition-colors border ${
                                        isSelected
                                          ? "bg-pista-700 text-white border-pista-700"
                                          : "bg-gray-50 text-gray-700 border-gray-200 hover:border-pista-500"
                                      }`}
                                    >
                                      {colIcon || colText}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              if (type === "html") {
                const rawHtml = interpolateHtml(element.html || "", model);
                const html = DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } });
                return (
                  <div
                    key={name}
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              }

              return (
                <div key={name} className="text-gray-500">
                  Unsupported question type: {type}
                </div>
              );
            })}
          </div>

          {currentPageName === "page_result" && model && (
            <SurveyPersonaCTA
              model={model}
              user={user}
              personaTitle={getPersonaValue(model, "persona_title")}
              personaIcon={getPersonaValue(model, "persona_icon")}
            />
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Navigation */}
          {currentPageName !== "page_result" && (
            <TypeformNavigation
              onNext={handleNext}
              onBack={handleBack}
              canGoBack={currentPageIndex > 0}
              canGoNext={canGoNext}
              isLastPage={currentPageIndex === visiblePages.length - 1}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </div>
  );
}
